import React from 'react';
import { Box, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { formatDistanceToNow } from 'date-fns';
import type { AppNotification } from '../types/notifications';

// An icon per notification type. A type this app doesn't know yet still renders, with the generic bell.
const TYPE_ICONS: Record<string, typeof NotificationsIcon> = {
  statement_available: ReceiptLongIcon,
};

function relativeTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true });
}

/** One notification, as the bell's dropdown and the inbox page both show it: unread ones bold and dotted. */
const NotificationListItem: React.FC<{
  notification: AppNotification;
  onOpen: (notification: AppNotification) => void;
  divider?: boolean;
}> = ({ notification, onOpen, divider = true }) => {
  const Icon = TYPE_ICONS[notification.type] ?? NotificationsIcon;
  const isUnread = !notification.read_at;

  return (
    <ListItemButton
      onClick={() => onOpen(notification)}
      divider={divider}
      sx={{ alignItems: 'flex-start', py: 1.5, bgcolor: isUnread ? 'action.hover' : undefined }}
    >
      <ListItemIcon sx={{ mt: 0.5, minWidth: 44 }}>
        <Icon color={isUnread ? 'primary' : 'action'} />
      </ListItemIcon>
      <ListItemText
        primary={notification.title}
        secondary={
          <>
            <Box component="span" sx={{ display: 'block' }}>{notification.body}</Box>
            <Box component="span" sx={{ display: 'block', mt: 0.5, typography: 'caption' }}>
              {relativeTime(notification.created_at)}
            </Box>
          </>
        }
        slotProps={{ primary: { sx: { fontWeight: isUnread ? 600 : 400 } } }}
      />
      {isUnread && (
        <Box
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0, mt: 1, ml: 1 }}
        >
          <Box component="span" sx={visuallyHidden}>Unread</Box>
        </Box>
      )}
    </ListItemButton>
  );
};

export default NotificationListItem;
